import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../../lib/utils'

const segmentedControlVariants = cva(
  'inline-flex items-center gap-1 p-1 rounded-lg bg-primary-300/10 transition-all duration-standard ease-apple',
  {
    variants: {
      size: {
        sm: 'h-8 text-sm',
        md: 'h-10 text-base',
        lg: 'h-12 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const segmentedControlItemVariants = cva(
  'relative inline-flex items-center justify-center gap-2 px-3 rounded-md font-medium transition-all duration-standard ease-apple cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1',
  {
    variants: {
      active: {
        true: 'bg-white text-primary-400 shadow-sm',
        false: 'bg-transparent text-ink-700 hover:text-primary-400',
      },
      size: {
        sm: 'h-6 text-sm px-2',
        md: 'h-8 text-base px-3',
        lg: 'h-10 text-base px-4',
      },
    },
    defaultVariants: {
      active: false,
      size: 'md',
    },
  }
)

export interface SegmentedControlOption<T extends string = string> {
  value: T
  label: string
  icon?: React.ReactNode
  disabled?: boolean
}

export interface SegmentedControlProps<T extends string = string>
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof segmentedControlVariants> {
  options: SegmentedControlOption<T>[]
  value: T
  onChange: (value: T) => void
  'aria-label'?: string
}

function SegmentedControlInner<T extends string = string>(
  {
    options,
    value,
    onChange,
    size,
    className,
    'aria-label': ariaLabel,
    ...props
  }: SegmentedControlProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(segmentedControlVariants({ size, className }))}
      {...props}
    >
      {options.map((option) => {
        const isActive = option.value === value
        const isDisabled = option.disabled ?? false

        return (
          <button
            key={option.value}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) {
                onChange(option.value)
              }
            }}
            className={cn(
              segmentedControlItemVariants({
                active: isActive,
                size,
              }),
              isDisabled && 'opacity-40 cursor-not-allowed'
            )}
          >
            {option.icon && (
              <span className="flex-shrink-0 -ml-0.5">{option.icon}</span>
            )}
            <span>{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}

export const SegmentedControl = React.forwardRef(SegmentedControlInner) as <
  T extends string = string,
>(
  props: SegmentedControlProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof SegmentedControlInner>
